import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import {
  Button,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  cn,
} from '@gu-corp/gu-ui';
import {
  Home,
  Wallet,
  Landmark,
  Globe,
  Code,
  LogOut,
  BookOpen,
  SquareArrowOutUpRight,
  Coins,
  Image,
  Crown,
  Shield,
  Newspaper,
  Megaphone,
  Calendar,
} from 'lucide-react';

import { AppRoutesEnum } from '~/enums/route.enum';
import Identicon from '~/components/identicon';
import { getEnv } from '~/env';
import { useMemberInfo } from '~/hooks/use-joc-club-contract';
import { TierGemIcon, TIER_ACCENTS } from '~/components/joc-club';
import { useAdminStatus } from '~/hooks/use-admin-status';

// Truncate address to show first 6 and last 4 characters
const truncateAddress = (address: string) => {
  if (!address) return '';
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

interface NavItem {
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  labelKey: string;
  external?: boolean;
}

interface AppSidebarProps {
  walletAddress?: string;
  onLogout?: () => void;
}

export function AppSidebar({ walletAddress, onLogout }: AppSidebarProps) {
  const router = useRouter();
  const { t } = useTranslation('common');

  const logoSrc = '/images/new_joc_icon.svg';

  // Home
  const homeNavItems: NavItem[] = useMemo(
    () => [{ href: AppRoutesEnum.DASHBOARD, icon: Home, labelKey: 'home' }],
    [],
  );

  // Asset management items
  const assetNavItems: NavItem[] = useMemo(
    () => [
      { href: AppRoutesEnum.WALLET, icon: Wallet, labelKey: 'wallet' },
      { href: AppRoutesEnum.NFTS, icon: Image, labelKey: 'nft' },
    ],
    [],
  );

  // Feature flags
  const isStakingEnabled = getEnv('NEXT_PUBLIC_FEATURE_STAKING_ENABLED') === 'true';

  // Service items
  const serviceNavItems: NavItem[] = useMemo(() => {
    const items: NavItem[] = [];
    items.push({ href: AppRoutesEnum.LENDING, icon: Landmark, labelKey: 'lending' });
    if (isStakingEnabled) {
      items.push({ href: AppRoutesEnum.STAKING, icon: Coins, labelKey: 'staking' });
    }
    items.push({ href: AppRoutesEnum.ECOSYSTEM, icon: Globe, labelKey: 'ecosystem' });
    return items;
  }, [isStakingEnabled]);

  // Developer items
  const developerNavItems: NavItem[] = useMemo(
    () => [{ href: AppRoutesEnum.DEVELOPMENT, icon: Code, labelKey: 'development' }],
    [],
  );

  // Admin items — visible to owners (env) and KV-stored admins, resolved
  // server-side via /api/admin/me (same check as the admin pages).
  const { isAdmin } = useAdminStatus();

  const adminNavItems: NavItem[] = useMemo(
    () => [
      { href: '/admin', icon: Crown, labelKey: 'admin_joc_club' },
      { href: '/admin/lending', icon: Landmark, labelKey: 'admin_lending' },
    ],
    [],
  );

  // Help/Document items
  const documentNavItems: NavItem[] = useMemo(
    () => [
      {
        href: 'https://japanopenchain.org/docs/dashboard/about',
        icon: BookOpen,
        labelKey: 'help',
        external: true,
      },
    ],
    [],
  );

  const isActive = (href: string) => {
    if (href === AppRoutesEnum.DASHBOARD) {
      return router.pathname === href;
    }
    return router.pathname.startsWith(href);
  };

  const handleNavigate = (href: string) => {
    router.push(href, href, { locale: router.locale });
  };

  const renderNavItems = (items: NavItem[]) => (
    <SidebarMenu>
      {items.map((item) => {
        const active = isActive(item.href);

        if (item.external) {
          return (
            <SidebarMenuItem key={item.href}>
              <SidebarMenuButton
                onClick={() => window.open(item.href, '_blank')}
                data-testid={`sidebar-nav-${item.labelKey}`}
              >
                <item.icon className="w-4 h-4" />
                <span className="flex-1">{t(item.labelKey)}</span>
                <SquareArrowOutUpRight className="w-3 h-3" />
              </SidebarMenuButton>
            </SidebarMenuItem>
          );
        }

        return (
          <SidebarMenuItem key={item.href}>
            <SidebarMenuButton
              isActive={active}
              onClick={() => handleNavigate(item.href)}
              data-testid={`sidebar-nav-${item.labelKey}`}
            >
              <item.icon className="w-4 h-4" />
              <span className="flex-1">{t(item.labelKey)}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        );
      })}
    </SidebarMenu>
  );

  return (
    <Sidebar>
      <SidebarHeader className="border-b border-sidebar-border">
        <div className="flex items-center gap-3 px-2 py-2">
          <img src={logoSrc} alt="JOC" className="h-8 w-8" />
          <div>
            <h1 className="text-sm font-semibold">JOC Club</h1>
            <p className="text-xs opacity-80">Japan Open Chain</p>
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>{renderNavItems(homeNavItems)}</SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>{t('sidebar.assets')}</SidebarGroupLabel>
          <SidebarGroupContent>{renderNavItems(assetNavItems)}</SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>{t('sidebar.services')}</SidebarGroupLabel>
          <SidebarGroupContent>{renderNavItems(serviceNavItems)}</SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>{t('sidebar.developer')}</SidebarGroupLabel>
          <SidebarGroupContent>{renderNavItems(developerNavItems)}</SidebarGroupContent>
        </SidebarGroup>

        {isAdmin && (
          <SidebarGroup>
            <SidebarGroupLabel>{t('sidebar.admin')}</SidebarGroupLabel>
            <SidebarGroupContent>{renderNavItems(adminNavItems)}</SidebarGroupContent>
          </SidebarGroup>
        )}

        <SidebarGroup>
          <SidebarGroupLabel>{t('sidebar.support')}</SidebarGroupLabel>
          <SidebarGroupContent>{renderNavItems(documentNavItems)}</SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border">
        <div className="px-2 py-2">
          {/* Mini SBT Pass */}
          <SidebarMiniPass walletAddress={walletAddress} />

          {walletAddress && (
            <div className="flex items-center gap-3 mb-2 p-2 rounded-lg bg-sidebar-accent">
              <Identicon address={walletAddress} size={32} />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground">{t('connected_wallet')}</p>
                <p className="text-sm font-mono">{truncateAddress(walletAddress)}</p>
              </div>
            </div>
          )}

          {onLogout && (
            <Button
              variant="outline"
              size="sm"
              className="w-full text-sidebar-foreground border-sidebar-foreground/30 hover:bg-sidebar-accent"
              onClick={onLogout}
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {t('logout')}
            </Button>
          )}
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}

// Mini SBT Pass for sidebar — same matte, tier-colored treatment as the
// dashboard's TierPassCard, just condensed for the sidebar footer.
function SidebarMiniPass({ walletAddress }: { walletAddress?: string }) {
  const { t } = useTranslation('common');
  const { member, tierName } = useMemberInfo(walletAddress);
  const router = useRouter();

  if (!member || member.tier === 0) return null;

  const accent = TIER_ACCENTS[tierName] || TIER_ACCENTS.White;

  return (
    <div
      className={cn(
        'relative mb-2 flex min-h-20 cursor-pointer flex-col overflow-hidden rounded-lg p-3 shadow-md ring-1 ring-white/10 transition-opacity hover:opacity-90',
        'bg-gradient-to-br',
        accent.bg,
        accent.glow,
      )}
      onClick={() => router.push('/')}
    >
      {/* Tone-on-tone watermark gem, tucked into the corner */}
      <TierGemIcon
        tier={tierName}
        withColor={false}
        className="pointer-events-none absolute -right-6 -top-6 h-28 w-28 text-white opacity-[0.1]"
      />
      <span className="relative text-[10px] uppercase tracking-wider text-white/55">
        {t('joc_club.soulbound_pass')}
      </span>
      <p className={cn('relative mt-auto text-sm font-bold', accent.text)}>{tierName}</p>
    </div>
  );
}

export default AppSidebar;
